/**
 * DashboardTable Component - Device data table with pagination and real-time status
 * 
 * Features:
 * - Paginated device data display with smart pagination
 * - Real-time status indicators and refresh functionality
 * - Device status chips with color coding
 * - Clickable device IDs to open device details drawer
 * - Error handling for API failures and no data scenarios
 * - Manual refresh with last updated timestamp
 * - Responsive table layout with device information
 * 
 * @author CTS - Ford PM UI Team
 
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  DashboardTableContainer,
  DashboardTableWrapper,
  DashboardNoDataMessage,
  DashboardPagination,
  DashboardPaginationControls,
  DashboardPaginationButton,
  DashboardPaginationInfo,
  DashboardTableActionsMUI,
  DashboardTableActionsRight,
  DashboardRefreshButton,
  DashboardLastRefreshedChip,
  DashboardCopyableDisplay,
  BlinkingGreenDot,
  DashboardMonospaceText,
  DashboardTableFilters,
  DashboardTableFilterButton
} from './Dashboard.styles';
import { Typography, Tooltip, Box } from '@mui/material';
import { Refresh, ChevronLeft, ChevronRight, ContentCopy } from '@mui/icons-material';
import { AppLabels } from '../../constant/constant';
import { copyToClipboard } from '../../utils/clipboardUtils.jsx';
import { renderTextWithTooltip, renderCriticalDeviceListWithDualSupply, formattedText } from '../../utils/TextTooltipUtils.jsx';
import StatusIndicator from '../../components/StatusIndicator.jsx';



/**
 * Dashboard table column configuration object
 * Defines table structure with key, header, render function, and visibility control
 */
const DASHBOARD_TABLE_COLUMNS = [
  {
    key: 'deviceName',
    header: AppLabels.dashboardTableLabels.deviceName,
    hideColumn: false,
    render: (row) => (
          <Typography sx={{ fontWeight: 700, color: 'rgb(19, 34, 52)', fontSize:'12px' }}>
            {row.deviceName}
          </Typography>
        )
  },
  {
    key: 'deviceMac',
    header: AppLabels.dashboardTableLabels.deviceMacHeader,
    render: (row, onOpenPlug) => (
      <DashboardCopyableDisplay>
        <Tooltip title={`${row.deviceMac} - Click to view details`} arrow>
          <DashboardMonospaceText clickable onClick={() => onOpenPlug(row.id)}>
            {row.deviceMac}
          </DashboardMonospaceText>
        </Tooltip>
        <Tooltip title={`Copy ${AppLabels.dashboardTableLabels.deviceMacHeader}`} arrow>
          <ContentCopy 
            className="copy-icon" 
            onClick={(event) => copyToClipboard(row.deviceMac, event)} 
          />
        </Tooltip>
      </DashboardCopyableDisplay>
    ),
    hideColumn: false
  },
  {
    key: 'locationId',
    header: AppLabels.dashboardTableLabels.locationHeader,
    render: (row) => renderTextWithTooltip(row.locationId, 'locationId'),
    hideColumn: false
  },
  {
    key: 'status',
    header: AppLabels.dashboardTableLabels.statusHeader,
    render: (row) => (
      <StatusIndicator
        status={row.status}
      />
    ),
    hideColumn: false
  },
  {
    key: 'smartPlugLocation',
    header: AppLabels.dashboardTableLabels.deviceTypeHeader,
    render: (row) => formattedText(row.smartPlugLocation),
    hideColumn: false
  },
  {
    key: 'modemId',
    header: AppLabels.dashboardTableLabels.modemIdHeader,
    render: (row) => (
      row.modemId && row.modemId !== AppLabels.notApplicable ? (
        <DashboardCopyableDisplay>
           <DashboardMonospaceText>{row.modemId}</DashboardMonospaceText>
        </DashboardCopyableDisplay>
      ) : (
        row.modemId
      )
    ),
    hideColumn: false
  },
  {
    key: 'criticalDeviceList',
    header: AppLabels.dashboardTableLabels.criticalDevicesHeader,
    render: (row) => renderCriticalDeviceListWithDualSupply(
      row.criticalDeviceList, 
      row.criticalDevices, 
      row.dualSupplyDevices
    ),
    hideColumn: false
  },
  {
    key: 'lastheartbeatat',
    header: AppLabels.dashboardTableLabels.lastHeartbeatHeader,
    render: (row) => row.lastheartbeatat,
    hideColumn: false
  }
];

/**
 * Filter visible columns based on hideColumn property
 * @returns {Array} Array of visible column configurations
 */
const getVisibleDashboardColumns = () => {
  return DASHBOARD_TABLE_COLUMNS.filter(column => !column.hideColumn);
};

/**
 * DashboardTable Component - Displays device data in a paginated table format
 * 
 * @param {Array} rows - Current page device data to display
 * @param {Array} fullRows - Complete filtered device data for count display
 * @param {number} page - Current page number
 * @param {number} totalPages - Total number of pages available
 * @param {Function} onPage - Callback for page navigation
 * @param {Function} onOpenPlug - Callback to open device details drawer
 * @param {string} error - Error message to display if API fails
 * @param {Function} onRefresh - Callback for manual data refresh
 * @param {Date} lastRefreshed - Timestamp of last data refresh
 * @param {boolean} isRealTimeEnabled - Whether real-time updates are active
 * @param {boolean} isRealTimePaused - Whether real-time updates are paused
 * @param {string} statusFilter - Current status filter value (all/chip-ok/chip-error)
 * @param {Function} onStatusFilterChange - Callback for status filter changes
 */
const DashboardTable = ({
  rows,
  fullRows,
  page,
  totalPages,
  onPage,
  onOpenPlug,
  error,
  onRefresh,
  lastRefreshed,
  isRealTimeEnabled,
  isRealTimePaused,
  statusFilter,
  onStatusFilterChange,
}) => {
  /**
   * Generates smart pagination numbers for display
   * Shows ellipsis (...) for large page counts and maintains context around current page
   * 
   * Logic:
   * - If total pages ≤ 5: Show all pages
   * - If current page ≤ 3: Show first 4 pages + ... + last page
   * - If current page ≥ (total-2): Show first + ... + last 4 pages
   * - Otherwise: Show first + ... + (current-1, current, current+1) + ... + last
   * 
   * @returns {Array} Array of page numbers and ellipsis strings
   */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (page <= 3) {
      // Smart pagination with ellipsis - Show first pages when near beginning
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...', totalPages);
    } else if (page >= totalPages - 2) {
      // Show last pages when near end
      pages.push(1, '...');
      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show middle pages with context
      pages.push(1, '...');
      for (let i = page - 1; i <= page + 1; i++) {
        pages.push(i);
      }
      pages.push('...', totalPages);
    }

    return pages;
  };

  // ========================================
  // ERROR STATE RENDERING
  // ========================================
  
  /**
   * Error state display - shows different styling for "no data" vs actual errors
   * Differentiates between API errors and empty result sets for better UX
   */
  if (error) {
    const isNoDevicesFound = error.includes(AppLabels.dashboardTableLabels.noDevicesFoundMessage);

    return (
      <DashboardTableContainer>
        <div style={{
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center'
        }}>
          {!isNoDevicesFound && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              <Typography style={{ fontSize: '24px', marginRight: '12px' }}>⚠️</Typography>
              <Typography style={{
                color: '#ef4444',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}>
                {AppLabels.dashboardTableLabels.errorLoadingDevices}
              </Typography>
            </div>
          )}

          <Typography style={{
            color: isNoDevicesFound ? '#666' : '#ef4444',
            fontSize: '1rem',
            marginBottom: '20px'
          }}>
            {error}
          </Typography>

          <Typography style={{
            color: '#666',
            fontSize: '0.9rem'
          }}>
            {isNoDevicesFound
              ? AppLabels.dashboardTableLabels.tryDifferentSelection
              : AppLabels.dashboardTableLabels.refreshPageMessage
            }
          </Typography>
        </div>
      </DashboardTableContainer>
    );
  }

  // ========================================
  // MAIN TABLE RENDERING
  // ========================================
  
  return (
    <DashboardTableContainer>
      {/* Table Actions Bar - Filter buttons, count display and refresh controls */}
      <DashboardTableActionsMUI>
        {/* Left side: Filter buttons and device count */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
          <DashboardTableFilters>
            <DashboardTableFilterButton
              active={statusFilter === 'all'}
              onClick={() => onStatusFilterChange('all')}
            >
              All
            </DashboardTableFilterButton>
            <DashboardTableFilterButton
              active={statusFilter === 'chip-error'}
              onClick={() => onStatusFilterChange('chip-error')}
            >
              Offline
            </DashboardTableFilterButton>
            <DashboardTableFilterButton
              active={statusFilter === 'chip-ok'}
              onClick={() => onStatusFilterChange('chip-ok')}
            >
              Online
            </DashboardTableFilterButton>
          </DashboardTableFilters>
          <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '11px', color: 'rgb(94, 110, 126)', display: 'none' }}>
            {AppLabels.plugDrawerLabels.showingText} {rows?.length || 0} {AppLabels.plugDrawerLabels.ofText} {fullRows?.length || 0}
          </Typography>
        </Box>

        {/* Right side: Refresh controls and status */}
        <DashboardTableActionsRight>
          <DashboardRefreshButton
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={onRefresh}
          >
            {AppLabels.dashboardTableLabels.refreshButton}
          </DashboardRefreshButton>
          {lastRefreshed && (
            <DashboardLastRefreshedChip
              label={
                <>
                  <BlinkingGreenDot />
                  {(() => {
                    const baseLabel = `Last refreshed: ${lastRefreshed.toLocaleString()}`;
                    if (!isRealTimeEnabled) return baseLabel;
                    if (isRealTimePaused) return `${baseLabel} (Auto-refresh paused)`;
                    return `${baseLabel}${AppLabels.dashboardTableLabels.autoRefreshLabelInTable.replace('$', AppLabels.dashboardTableLabels.autoRefreshValue)}`;
                  })()}
                </>
              }
              variant="outlined"
              size="small"
            />
          )}
        </DashboardTableActionsRight>
      </DashboardTableActionsMUI>

      {/* Main Data Table */}
      <DashboardTableWrapper>
        <table>
          {/* Table Header - Dynamic column rendering */}
          <thead>
            <tr>
              {getVisibleDashboardColumns().map(column => (
                <th key={column.key}>{column.header}</th>
              ))}
            </tr>
          </thead>
          {/* Table Body - Device data rows */}
          <tbody>
            {!rows || rows.length === 0 ? (
              /* No Data State */
              <tr>
                <td colSpan={getVisibleDashboardColumns().length}>
                  <DashboardNoDataMessage>
                    <div className="title">{AppLabels.dashboardTableLabels.noDevicesFoundMessage}</div>
                    <div className="subtitle">
                      {AppLabels.dashboardTableLabels.tryDifferentSelection}
                    </div>
                  </DashboardNoDataMessage>
                </td>
              </tr>
            ) : (
              /* Device Data Rows - Dynamic column rendering */
              rows.map(r => (
                <tr key={r.id}>
                  {getVisibleDashboardColumns().map(column => (
                    <td key={column.key}>
                      {column.render(r, onOpenPlug)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DashboardTableWrapper>

      {/* Pagination Controls - Only show if multiple pages exist */}
      {totalPages > 1 && (
        <DashboardPagination>
          {/* Page Information Display */}
          <DashboardPaginationInfo>
            Page {page} of {totalPages}
          </DashboardPaginationInfo>

          {/* Pagination Navigation Controls */}
          <DashboardPaginationControls>
            {/* Previous Page Button */}
            <DashboardPaginationButton
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
              startIcon={<ChevronLeft />}
            >
              Prev
            </DashboardPaginationButton>

            {/* Smart Page Numbers with Ellipsis */}
            {getPageNumbers().map((pageNum, index) => (
              pageNum === '...' ? (
                /* Ellipsis for skipped pages */
                <Typography key={`ellipsis-${pageNum}`} sx={{ color: '#9aa3c7', padding: '0 8px' }}>
                  ...
                </Typography>
              ) : (
                /* Clickable Page Number */
                <DashboardPaginationButton
                  key={pageNum}
                  className={page === pageNum ? 'active' : ''}
                  onClick={() => onPage(pageNum)}
                >
                  {pageNum}
                </DashboardPaginationButton>
              )
            ))}

            {/* Next Page Button */}
            <DashboardPaginationButton
              disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}
              endIcon={<ChevronRight />}
            >
              Next
            </DashboardPaginationButton>
          </DashboardPaginationControls>
        </DashboardPagination>
      )}
    </DashboardTableContainer>
  );
};

// PropTypes validation
DashboardTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  fullRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPage: PropTypes.func.isRequired,
  onOpenPlug: PropTypes.func.isRequired,
  error: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  lastRefreshed: PropTypes.instanceOf(Date),
  isRealTimeEnabled: PropTypes.bool.isRequired,
  isRealTimePaused: PropTypes.bool.isRequired,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired
};

export default DashboardTable;
